

(() => {
  if (window.__nekoCardSwatchesV2) return;
  window.__nekoCardSwatchesV2 = true;
  const selector = '[data-neko-card-swatches]';
  const buttonSelector = '[data-neko-card-swatch]';
  const cache = new Map();
  const pending = new WeakMap();
  const watched = new WeakSet();
  const queue = [];
  let running = 0;
  let budget = 12;

  function pump() {
    while (running < 2 && queue.length) {
      const task = queue.shift();
      running++;
      task().finally(() => { running--; pump(); });
    }
  }

  function prepare(button, urgent = false) {
    const card = button.closest('.card-wrapper');
    const image = card?.querySelector('.card__media .media > img:first-child');
    if (!image || !button.dataset.imageSrc) return Promise.resolve(null);
    const sizes = Math.ceil(image.getBoundingClientRect().width || 360) + 'px';
    const srcset = button.dataset.imageSrcset || '';
    const src = button.dataset.imageSrc;
    const key = src + '|' + srcset + '|' + sizes;
    if (cache.has(key)) {
      const entry = cache.get(key);
      if (urgent) {
        const index = queue.indexOf(entry.task);
        if (index >= 0) { queue.splice(index, 1); entry.task(); }
      }
      return entry.promise;
    }
    let resolve;
    const promise = new Promise(done => { resolve = done; });
    const task = () => new Promise(done => {
      const preload = new Image();
      preload.decoding = 'async';
      preload.fetchPriority = urgent ? 'high' : 'low';
      let settled = false;
      const finish = (ok) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        preload.onload = preload.onerror = null;
        if (!ok) cache.delete(key);
        resolve(ok ? { src, srcset, sizes } : null);
        done();
      };
      const timer = setTimeout(() => finish(false), 10000);
      preload.onload = () => {
        if (preload.decode) preload.decode().catch(() => {}).then(() => finish(true));
        else finish(true);
      };
      preload.onerror = () => finish(false);
      preload.sizes = sizes;
      if (srcset) preload.srcset = srcset;
      preload.src = src;
    });
    cache.set(key, { promise, task });
    if (cache.size > 64) cache.delete(cache.keys().next().value);
    if (urgent) task();
    else queue.push(task);
    pump();
    return promise;
  }

  function speculativeAllowed() {
    const connection = navigator.connection;
    return !document.hidden && !connection?.saveData &&
      !/^(slow-2g|2g|3g)$/.test(connection?.effectiveType || '');
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest?.(buttonSelector);
    if (!button) return;
    event.preventDefault();
    const card = button.closest('.card-wrapper');
    const group = button.closest(selector);
    if (!card || !group) return;
    const ticket = {};
    pending.set(card, ticket);
    group.setAttribute('aria-busy', 'true');
    const source = await prepare(button, true);
    if (pending.get(card) !== ticket || !card.isConnected) return;
    group.removeAttribute('aria-busy');
    if (button.dataset.imageSrc && !source) return;
    const image = card.querySelector('.card__media .media > img:first-child');
    if (image && source) {
      image.sizes = source.sizes;
      image.srcset = source.srcset;
      image.src = source.src;
      image.alt = button.dataset.imageAlt || '';
      const frame = image.parentElement;
      const framing = button.dataset.imageFrame || '';
      for (const property of ['--neko-frame-scale', '--neko-frame-x', '--neko-frame-y', '--neko-frame-bg']) frame.style.removeProperty(property);
      frame.classList.toggle('neko-image-frame', Boolean(framing));
      if (framing) frame.style.cssText += ';' + framing;
      frame.classList.add('neko-color-selected');
    }
    if (button.dataset.variantUrl) {
      card.querySelectorAll('a[href]').forEach(link => {
        if (link.href.includes('/products/')) link.href = button.dataset.variantUrl;
      });
      card.querySelectorAll('modal-opener [data-product-url]').forEach(opener => {
        opener.dataset.productUrl = button.dataset.variantUrl;
      });
    }
    group.querySelectorAll(buttonSelector).forEach(swatch => {
      const active = swatch === button;
      swatch.classList.toggle('is-active', active);
      swatch.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  });

  for (const eventName of ['pointerover', 'focusin', 'touchstart']) {
    document.addEventListener(eventName, event => {
      const button = event.target.closest?.(buttonSelector);
      if (button && speculativeAllowed()) prepare(button, true);
    }, { passive: true });
  }

  function start() {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting || !speculativeAllowed() || budget <= 0) continue;
        observer.unobserve(entry.target);
        const warm = () => {
          if (!speculativeAllowed() || !entry.target.isConnected) return;
          const rect = entry.target.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > window.innerHeight) return;
          const buttons = Array.from(entry.target.querySelectorAll(buttonSelector))
            .filter(button => button.getAttribute('aria-pressed') !== 'true' && button.dataset.imageSrc)
            .slice(0, 2);
          for (const button of buttons) {
            if (budget <= 0) break;
            budget--;
            prepare(button);
          }
        };
        if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 1500 });
        else setTimeout(warm, 250);
      }
    }, { threshold: 0.1 });
    const watch = root => {
      if (!(root instanceof Element) && root !== document) return;
      const groups = root.matches?.(selector) ? [root] : root.querySelectorAll(selector);
      for (const group of groups) {
        if (watched.has(group)) continue;
        watched.add(group);
        observer.observe(group);
      }
    };
    watch(document);
    new MutationObserver(records => {
      for (const record of records) for (const node of record.addedNodes) watch(node);
    }).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', event => watch(event.target));
  }
  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
})();


